import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import styles from "./EstimateLayoutStep1.module.scss";
import classNames from "classnames/bind";
import TitleEditor from "~/components/common/form/titleEdit/TitleEditor";
import FormInput from "~/components/common/form/formInput/FormInput";
import FormDatePicker from "~/components/common/form/base-date-pickerV2/FormDatePicker";
import BaseUploadFileV2 from "~/components/common/base-upload-file-v2/BaseUploadFileV2";
import OutlinedButton from "~/components/common/outlined-button/OutlinedButton";
import { PlusIcon, TrashIcon } from "~/assets/icons";
import type { EstimateLayoutStep1FormData as Step1FormData } from "~/utils/interface/estimateSetting";
import { notify } from "~/utils/helper";

dayjs.extend(relativeTime);
const cx = classNames.bind(styles);

interface EstimateLayoutStep1Props {
    onStep1DataChange?: (data: Step1FormData) => void;
    initialData?: Step1FormData | null;
}

export type EstimateLayoutStep1Ref = {
    validate: () => boolean;
};

const ESTIMATE_NAME_REQUIRED = "Estimate Name is required";

const EstimateLayoutStep1 = forwardRef<EstimateLayoutStep1Ref, EstimateLayoutStep1Props>(({ onStep1DataChange, initialData }, ref) => {
    const uploadRef = useRef<any>(null);

    const DEFAULT_TITLE_STEP = "Christian Brothers Roofing Proposal";

    const [formData, setFormData] = useState<Step1FormData | undefined>(() => ({
        ...initialData,
        titleStep1: initialData?.titleStep1 ?? DEFAULT_TITLE_STEP,
    }));

    const [error, setError] = useState({
        name: '',
        date: '',
        file: '',
        company: "",
        firstName: '',
        lastName: '',
        address: '',
        city: '',
        province: '',
        code: '',
    });

    const validateEstimateName = (name: string | undefined): string => {
        const trimmed = (name ?? "").trim();
        if (!trimmed) return ESTIMATE_NAME_REQUIRED;
        return "";
    };

    useImperativeHandle(ref, () => ({
        validate: () => {
            const nameError = validateEstimateName(formData?.name);
            setError((prev) => ({ ...prev, name: nameError }));
            return nameError === "";
        },
    }), [formData?.name]);

    const handleUpdateData = (update: Partial<Step1FormData>) => {
        const data: Step1FormData = { ...formData, ...update } as Step1FormData;
        setFormData(data);
        onStep1DataChange?.(data);

        if (Object.prototype.hasOwnProperty.call(update, "name")) {
            const nameError = validateEstimateName(data.name);
            setError((prev) => ({ ...prev, name: nameError }));
        }
    };

    const handleAddCertificateLogo = () => {
        if (formData?.certificateLogo) return;
        handleUpdateData({
            certificateLogo: { id: `cert-logo-${Date.now()}` },
        });
    };

    const handleCertificateLogoChange = (data: { file?: File; fileUrl?: string }) => {
        handleUpdateData({
            certificateLogo:
                data.file || data.fileUrl
                    ? { id: formData?.certificateLogo?.id ?? "cert-logo", file: data.file, fileUrl: data.fileUrl }
                    : undefined,
        });
    };

    const handleRemoveCertificateLogo = () => {
        handleUpdateData({ certificateLogo: undefined });
    };

    return (
        <div className={cx('estimate-layout-step-1-container')}>
            <div className={cx('header', 'flex items-center justify-between')}>
                <TitleEditor value={formData?.titleStep1}
                    name={""}
                    isViewMode={false}
                    placeholder={""}
                    inputkey={""}
                    onChange={(value) => {
                        handleUpdateData({
                            ...formData,
                            titleStep1: value
                        })
                    }}
                    styleView={{
                        fontSize: "20px",
                        fontWeight: 700,
                    }}
                />
            </div>

            <div className={cx('content')}>
                <div className={cx('estimate-row', 'mb-[32px]')}>
                    <div className="w-[50%]">
                        <div className={cx("base-input")}>
                            <FormInput
                                name={"name"}
                                isViewMode={false}
                                text={"Estimate Name"}
                                placeholder={"Enter estimate name"}
                                inputkey={"name"}
                                value={formData?.name}
                                error={error.name}
                                onChange={(e) => {
                                    handleUpdateData({
                                        ...formData,
                                        name: e.target.value
                                    })
                                }}
                            />
                        </div>
                    </div>
                    <div className="w-[50%]">
                        <FormDatePicker
                            text={"Date"}
                            selectRange={false}
                            value={formData?.date}
                            error={error.date !== ""}
                            onChange={(e) => {
                                handleUpdateData({
                                    ...formData,
                                    date: e ?? undefined
                                })
                            }} />
                    </div>
                </div>

                <div className={cx('estimate-row', 'mb-[32px]')}>
                    <div className="w-full">
                        <div className={cx("base-input")}>
                            <h3 className={cx({
                                "input-label": true
                            })}>Primary Image</h3>
                        </div>

                        <div className="mt-[16px]" >
                            <BaseUploadFileV2
                                ref={uploadRef}
                                url={formData?.fileUrl}
                                isViewMode={false}
                                errorMessage={error.file}
                                onChange={(data: any) => {
                                    const file = data.file as File | undefined;

                                    if (file && file.size > 10 * 1024 * 1024) {
                                        setError((prev) => ({
                                            ...prev,
                                            file: "File size must be less than 10MB",
                                        }));
                                        notify("error", "File size must be less than 10MB");
                                        handleUpdateData({
                                            ...formData,
                                            file: undefined,
                                            fileUrl: "",
                                        });
                                        return;
                                    }

                                    setError((prev) => ({
                                        ...prev,
                                        file: "",
                                    }));

                                    handleUpdateData({
                                        ...formData,
                                        file: data.file,
                                        fileUrl: data.fileUrl,
                                    })
                                }}
                            />
                        </div>
                    </div>

                </div>

                <div className={cx('estimate-row', 'mb-[32px]')}>
                    <div className="w-full">
                        {!formData?.certificateLogo ? (
                            <OutlinedButton
                                title="Add Certificate Logo"
                                onClick={handleAddCertificateLogo}
                                icon={<PlusIcon className={cx("plus-icon")} />}
                                className={cx("add-certificate-logo-button")}
                            />
                        ) : (
                            <>
                                <div className={cx("base-input")}>
                                    <h3 className={cx("input-label")}>Certificate Logo</h3>
                                </div>
                                <div className={cx('certificate-logos-list', 'mt-[16px]')}>
                                    <div className={cx('certificate-logo-item')}>
                                        <div className={cx('certificate-logo-upload')}>
                                            <BaseUploadFileV2
                                                url={formData.certificateLogo.fileUrl}
                                                isViewMode={false}
                                                onChange={(data: any) =>
                                                    handleCertificateLogoChange({
                                                        file: data.file,
                                                        fileUrl: data.fileUrl,
                                                    })
                                                }
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            className={cx('remove-certificate-logo')}
                                            onClick={handleRemoveCertificateLogo}
                                            aria-label="Remove certificate logo"
                                        >
                                            <TrashIcon />
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <div className={cx('sub-title', 'mb-[23px]')}>
                    <h2>Additional Information</h2>
                </div>

                <div className={cx('estimate-row', 'mb-[16px]')}>
                    <FormInput
                        name={"company"}
                        text={"Company name (Optional)"}
                        placeholder={"Company name"}
                        inputkey={"company"}
                        value={formData?.company}
                        error={error.company}
                        isViewMode={false}
                        onChange={(e) => {
                            handleUpdateData({
                                ...formData,
                                company: e.target.value
                            })
                        }}
                    />

                    <FormInput
                        name={"firstName"}
                        text={"First Name"}
                        placeholder={"First name"}
                        inputkey={"firstName"}
                        value={formData?.firstName}
                        error={error.firstName}
                        isViewMode={false}
                        onChange={(e) => {
                            handleUpdateData({
                                ...formData,
                                firstName: e.target.value
                            })
                        }}
                    />
                    <FormInput
                        name={"lastName"}
                        text={"Last Name"}
                        placeholder={"Last name"}
                        inputkey={"lastName"}
                        value={formData?.lastName}
                        error={error.lastName}
                        isViewMode={false}
                        onChange={(e) => {
                            handleUpdateData({
                                ...formData,
                                lastName: e.target.value
                            })
                        }}
                    />
                </div>

                <div className={cx('estimate-row', 'mb-[16px]')}>
                    <FormInput
                        name={"address"}
                        text={"Address"}
                        placeholder={"Address"}
                        inputkey={"address"}
                        value={formData?.address}
                        error={error.address}
                        isViewMode={false}
                        onChange={(e) => {
                            handleUpdateData({
                                ...formData,
                                address: e.target.value
                            })
                        }}
                    />

                    <FormInput
                        name={"city"}
                        text={"City"}
                        placeholder={"City"}
                        inputkey={"city"}
                        value={formData?.city}
                        error={error.city}
                        isViewMode={false}
                        onChange={(e) => {
                            handleUpdateData({
                                ...formData,
                                city: e.target.value
                            })
                        }}
                    />

                    <FormInput
                        name={"province"}
                        text={"State/ Province"}
                        placeholder={"State/ Province"}
                        inputkey={"province"}
                        value={formData?.province}
                        error={error.province}
                        isViewMode={false}
                        onChange={(e) => {
                            handleUpdateData({
                                ...formData,
                                province: e.target.value
                            })
                        }}
                    />
                </div>

                <div className={cx('estimate-row')}>
                    <FormInput
                        name={"code"}
                        text={"Zip code / Postal code"}
                        placeholder={"Zip code / Postal code"}
                        inputkey={"company"}
                        isViewMode={false}
                        error={error.code}
                        value={formData?.code}
                        onChange={(e) => {
                            handleUpdateData({
                                ...formData,
                                code: e.target.value
                            })
                        }}
                    />

                    <div className="w-full">
                    </div>

                    <div className="w-full">
                    </div>
                </div>
            </div>
        </div>
    );
});

EstimateLayoutStep1.displayName = "EstimateLayoutStep1";

export default EstimateLayoutStep1;